import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  Box,
  Collapse,
  Group,
  NavLink,
  Stack,
  Tabs,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import {
  WorkerInboxTabKeys,
  WorkerInboxTabs,
} from '@th-types/inbox-reviews.type';
import { useWorkerInboxIndicators } from '@worker/hooks/useWorkerInbox';
import { BASE_PATH } from '@utils/Environment';
import TodoTab from '@worker/components/WorkerInbox/Tabs/TodoTab';
import OtherTab from '@worker/components/WorkerInbox/Tabs/OtherTab';
import JobMessagesTab from '@worker/components/WorkerInbox/Tabs/JobMessagesTab';
import DirectMessagesTab from '@worker/components/WorkerInbox/Tabs/DirectMessagesTab';
import RateCompanyDrawer from '@worker/components/RateCompanyDrawer/RateCompanyDrawer';
import RequestRatingBackModal from '@worker/components/RateCompanyDrawer/RequestRatingBackModal';

const VALID_TAB_KEYS = Object.values(WorkerInboxTabKeys);

const TABS_CONFIG = [
  { value: WorkerInboxTabKeys.TODO, label: WorkerInboxTabs.TODO },
  { value: WorkerInboxTabKeys.OTHER, label: WorkerInboxTabs.OTHER },
  {
    value: WorkerInboxTabKeys.JOB_MESSAGES,
    label: WorkerInboxTabs.JOB_MESSAGES,
  },
  {
    value: WorkerInboxTabKeys.DIRECT_MESSAGES,
    label: WorkerInboxTabs.DIRECT_MESSAGES,
  },
] as const;

function TabLabel({
  label,
  count,
}: {
  label: string;
  count: number | undefined;
}) {
  return (
    <Text component="span" size="sm">
      {label}
      {count != null && count > 0 && (
        <Text component="span" fw={700}>
          {' '}
          ({count})
        </Text>
      )}
    </Text>
  );
}

export default function WorkerInbox() {
  const { activeTab: urlTab } = useParams<{ activeTab?: WorkerInboxTabKeys }>();
  const navigate = useNavigate();
  const [mobileNavExpanded, setMobileNavExpanded] = useState(false);

  // URL is the source of truth for the active tab so the first render mounts
  // the correct tab panel and we avoid firing queries for the default tab.
  const activeTab = useMemo<WorkerInboxTabKeys>(() => {
    if (urlTab && VALID_TAB_KEYS.includes(urlTab)) {
      return urlTab;
    }
    return WorkerInboxTabKeys.TODO;
  }, [urlTab]);

  useEffect(() => {
    if (urlTab && VALID_TAB_KEYS.includes(urlTab)) return;
    navigate(`${BASE_PATH}inbox/${WorkerInboxTabKeys.TODO}`, { replace: true });
  }, [urlTab, navigate]);

  const { data: indicators } = useWorkerInboxIndicators();

  const handleTabChange = (newTab: string | null) => {
    if (!newTab) return;
    navigate(`${BASE_PATH}inbox/${newTab}`);
    setMobileNavExpanded(false);
  };

  const getCounterForTab = (tabKey: WorkerInboxTabKeys): number | undefined => {
    switch (tabKey) {
      case WorkerInboxTabKeys.TODO:
        return indicators?.todoCount;
      case WorkerInboxTabKeys.OTHER:
        return indicators?.notificationCount;
      case WorkerInboxTabKeys.JOB_MESSAGES:
        return indicators?.messageCount;
      case WorkerInboxTabKeys.DIRECT_MESSAGES:
        return indicators?.directMessagesCount;
      default:
        return undefined;
    }
  };

  const activeTabConfig = TABS_CONFIG.find((t) => t.value === activeTab);
  const activeCount = getCounterForTab(activeTab);

  return (
    <Stack gap={0} flex={1}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        keepMounted={false}
        style={{ width: '100%' }}
      >
        {isMobile ? (
          <Box px={{ base: 'xs', sm: 'md' }} pt="sm">
            <UnstyledButton
              onClick={() => setMobileNavExpanded((prev) => !prev)}
              w="100%"
              pb="sm"
            >
              <Group justify="space-between" align="center">
                <Text fw={700} size="lg">
                  {activeTabConfig?.label ?? 'Inbox'}
                  {activeCount != null &&
                    activeCount > 0 &&
                    ` (${activeCount})`}
                </Text>
                {mobileNavExpanded ? (
                  <IoChevronUp size={20} />
                ) : (
                  <IoChevronDown size={20} />
                )}
              </Group>
            </UnstyledButton>
            <Collapse in={mobileNavExpanded}>
              <Stack gap={0} mb="sm">
                {TABS_CONFIG.map(({ value, label }) => {
                  const count = getCounterForTab(value);
                  const countLabel =
                    count != null && count > 0 ? ` (${count})` : '';
                  return (
                    <NavLink
                      key={value}
                      label={`${label}${countLabel}`}
                      active={value === activeTab}
                      onClick={() => handleTabChange(value)}
                      color="var(--yellow)"
                    />
                  );
                })}
              </Stack>
            </Collapse>
          </Box>
        ) : (
          <Tabs.List w="100%">
            {TABS_CONFIG.map(({ value, label }) => (
              <Tabs.Tab key={value} value={value} px="md" style={{ flex: 1 }}>
                <TabLabel label={label} count={getCounterForTab(value)} />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        )}

        <Tabs.Panel
          value={WorkerInboxTabKeys.TODO}
          px={isMobile ? 0 : undefined}
        >
          <TodoTab />
        </Tabs.Panel>

        <Tabs.Panel value={WorkerInboxTabKeys.OTHER}>
          <OtherTab />
        </Tabs.Panel>

        <Tabs.Panel value={WorkerInboxTabKeys.JOB_MESSAGES}>
          <JobMessagesTab />
        </Tabs.Panel>

        <Tabs.Panel value={WorkerInboxTabKeys.DIRECT_MESSAGES}>
          <DirectMessagesTab />
        </Tabs.Panel>
      </Tabs>
      <RateCompanyDrawer />
      <RequestRatingBackModal />
    </Stack>
  );
}
